<template>
    <div class="stl-ledger">
        <p class="stl-ledger-help">
            <strong>총요금</strong>은 해당 월 고지서 총액입니다.
            <strong>사용량 배분 합</strong>은 세대별 사용량에 따른 배분액의 합이고,
            <strong>세대 청구 합계</strong>는 수동 조정까지 반영한 실제 청구액 합입니다.
            원 단위 절삭 때문에 <strong>잔차</strong>가 소액 발생할 수 있습니다.
        </p>

        <!-- ─── 필터 행 ─────────────────────────────────────────────── -->
        <div style="display: flex; align-items: flex-end; gap: 12px; margin-bottom: 18px; flex-wrap: wrap;">
            <div class="stl-field">
                <label>보기 모드</label>
                <div class="stl-seg">
                    <button type="button" :class="{ on: mode === 'monthly' }" @click="mode = 'monthly'">월별 요약</button>
                    <button type="button" :class="{ on: mode === 'household' }" @click="mode = 'household'">세대별 타임라인</button>
                </div>
            </div>

            <div class="stl-field">
                <label>기간</label>
                <div style="display: inline-flex; align-items: center; gap: 6px;">
                    <input v-model="ymFrom" type="month" class="stl-input num" style="width: 130px;" @change="load" />
                    <span class="dim2">→</span>
                    <input v-model="ymTo" type="month" class="stl-input num" style="width: 130px;" @change="load" />
                </div>
            </div>

            <div v-if="mode === 'monthly'" class="stl-field">
                <label>상태</label>
                <select v-model="statusFilter" class="stl-select" style="width: 100px;">
                    <option value="FINAL">확정</option>
                    <option value="ALL">전체</option>
                </select>
            </div>

            <button type="button" class="stl-btn stl-btn-ghost" :disabled="loading" title="새로고침" @click="load">
                <i class="fa fa-refresh"></i>
            </button>
            <span style="flex: 1;"></span>
            <span class="stl-hint">{{ exportHint }}</span>
            <button type="button" class="stl-btn" :disabled="exporting || !canExport" @click="exportCsv">
                <b-spinner v-if="exporting" small />
                Excel
            </button>
        </div>

        <!-- ─── 월별 요약 ───────────────────────────────────────────── -->
        <template v-if="mode === 'monthly'">
            <div v-if="loading" class="stl-loading"><b-spinner small variant="secondary" /><span>조회 중...</span></div>
            <div v-else-if="!historyRows.length" class="stl-empty">
                <i class="fa fa-inbox"></i>
                <p>{{ emptyMonthlyMessage }}</p>
                <button
                    v-if="statusFilter === 'FINAL'"
                    type="button"
                    class="stl-btn stl-btn-primary"
                    style="margin-top: 12px;"
                    @click="$emit('go-tab', 'run')"
                >
                    정산 실행으로 이동
                </button>
            </div>
            <div v-else class="stl-mon" :class="{ 'stl-mon--all': isAll }">
                <div class="stl-mon-head">
                    <div class="mrow hd">
                        <span class="microcap">청구월</span>
                        <span v-if="isAll" class="microcap">에너지</span>
                        <span class="microcap">상태</span>
                        <span class="microcap r">총요금</span>
                        <span class="microcap r">사용량 배분 합</span>
                        <span class="microcap r">세대 청구 합계</span>
                        <span class="microcap r">잔차</span>
                        <span class="microcap r">세대수</span>
                        <span class="microcap">확정일</span>
                    </div>
                </div>
                <div class="stl-mon-body">
                    <div
                        v-for="row in historyRows"
                        :key="row.settlementIdx"
                        class="mrow clickable"
                        title="클릭하여 해당 월 세대별 내역 보기"
                        @click="openMonthMatrixModal(row)"
                    >
                        <span class="mm">{{ fmtYm(row.regYm) }}</span>
                        <span v-if="isAll" class="dim">{{ energyRowLabel(row.energySource) }}</span>
                        <span><span :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span></span>
                        <span class="r"><span class="cv" style="font-size: 14px;">{{ fmtWon(row.kepcoTotalAmount) }}</span></span>
                        <span class="r"><span class="cv dim">{{ fmtWon(row.householdSumIndividual) }}</span></span>
                        <span class="r"><span class="cv">{{ fmtWon(row.totalAmountSum) }}</span></span>
                        <span class="r"><span class="cv dim" :title="'총요금 − 세대 청구 합계'">{{ fmtWon(rowFloorRemainder(row)) }}</span></span>
                        <span class="r"><span class="cv dim">{{ row.detailCount }}</span></span>
                        <span class="mrow-date num dim">{{ row.confirmedAt ? String(row.confirmedAt).slice(0, 10) : "-" }}</span>
                    </div>
                </div>
            </div>
        </template>

        <!-- ─── 세대별 타임라인 ──────────────────────────────────────── -->
        <div v-else class="stl-sec stl-ledger-sec">
            <div class="stl-sec-h">
                <span class="t">세대별 타임라인</span>
                <span class="stl-chip plain">{{ energySourceLabel }}</span>
                <span class="sp"></span>
                <button type="button" class="stl-btn stl-btn-sm" :disabled="!canPrevHousehold" @click="stepHousehold(-1)">
                    <i class="fa fa-chevron-left"></i>
                </button>
                <div class="stl-household-picker">
                    <input
                        v-model="householdSearch"
                        type="search"
                        class="stl-input"
                        style="width: 120px;"
                        placeholder="호 검색"
                        @keydown.enter.prevent="pickFirstFiltered"
                    />
                    <select
                        v-model="selectedSysNodeIdx"
                        class="stl-select"
                        style="width: 200px;"
                        :disabled="loadingHouseholds || !filteredHouseholdOptions.length"
                    >
                        <option value="">— 호를 선택하세요 —</option>
                        <option v-for="h in filteredHouseholdOptions" :key="h.sysNodeIdx" :value="h.sysNodeIdx">
                            {{ h.roomName }}{{ h.isOccupied === "N" ? " (공실)" : "" }}
                        </option>
                    </select>
                </div>
                <button type="button" class="stl-btn stl-btn-sm" :disabled="!canNextHousehold" @click="stepHousehold(1)">
                    <i class="fa fa-chevron-right"></i>
                </button>
            </div>
            <div class="stl-sec-b stl-ledger-sec-b">
                <div v-if="loadingHouseholds" class="stl-loading"><b-spinner small variant="secondary" /><span>세대 목록 조회 중...</span></div>
                <div v-else-if="!sortedHouseholdOptions.length" class="stl-empty">
                    <p>등록된 세대가 없습니다. 단지정보에서 <strong>계통 세대 재동기화</strong>를 실행하세요.</p>
                    <button type="button" class="stl-btn stl-btn-primary" style="margin-top: 12px;" @click="$emit('open-site-settings')">
                        단지정보 열기
                    </button>
                </div>
                <HouseholdHistoryTimeline
                    v-else
                    :data="householdHistory"
                    :loading="loading"
                    :usage-unit="timelineUsageUnit"
                    :show-area-column="showTimelineAreaCol"
                    :show-energy-column="isAll"
                    @select="openHouseholdModalFromTimeline"
                />
            </div>
        </div>

        <LedgerMonthMatrixModal
            v-model="monthMatrixModal.show"
            :reg-ym="monthMatrixModal.regYm"
            :matrix="monthMatrixModal.matrix"
            :loading="monthMatrixModal.loading"
            :energy-source="monthMatrixModal.matrixEnergySource || energySource"
            :has-prev="monthMatrixModal.index > 0"
            :has-next="monthMatrixModal.index >= 0 && monthMatrixModal.index < monthMatrixNavRows.length - 1"
            @prev="stepMonthMatrix(-1)"
            @next="stepMonthMatrix(1)"
            @select-row="openHouseholdModalFromMonthMatrix"
        />

        <SettlementHouseholdModal
            v-model="householdModal.show"
            :site="site"
            :reg-ym="householdModal.regYm"
            :energy-source="householdModal.modalEnergySource || energySource"
            :sys-node-idx="householdModal.sysNodeIdx"
            :room-name="householdModal.roomName"
            :billing-start-at="householdModal.billingStartAt"
            :billing-end-at="householdModal.billingEndAt"
        />
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import HouseholdHistoryTimeline from "../components/HouseholdHistoryTimeline.vue";
import LedgerMonthMatrixModal from "../components/LedgerMonthMatrixModal.vue";
import SettlementHouseholdModal from "../components/SettlementHouseholdModal.vue";
import {
    fmtWon,
    fmtUsage,
    fmtArea,
    fmtYm,
    unitLabel,
    ymToRegYm,
    shiftRegYm,
    currentRegYm,
    statusLabel,
    statusClass,
    parseApiError,
    sortHouseholdsByRoom,
} from "../utils/settlementFormat.js";
import {
    buildHouseholdCsvRows,
    downloadCsvBlob,
    floorRemainder,
} from "../utils/ledgerExport.js";

export default {
    name: "SettlementLedgerTab",
    components: {
        HouseholdHistoryTimeline,
        LedgerMonthMatrixModal,
        SettlementHouseholdModal,
    },
    props: {
        site: { type: Object, required: true },
        energySource: { type: String, default: "Elec" },
    },
    data() {
        const to = currentRegYm();
        const from = shiftRegYm(to, -11);
        return {
            mode: "monthly",
            ymFrom: `${from.slice(0, 4)}-${from.slice(4, 6)}`,
            ymTo: `${to.slice(0, 4)}-${to.slice(4, 6)}`,
            statusFilter: "FINAL",
            exporting: false,
            loading: false,
            loadingHouseholds: false,
            historyRows: [],
            households: [],
            selectedSysNodeIdx: "",
            householdSearch: "",
            householdHistory: null,

            monthMatrixModal: {
                show: false,
                loading: false,
                regYm: "",
                matrix: null,
                index: -1,
                matrixEnergySource: null,
            },
            householdModal: {
                show: false,
                regYm: "",
                sysNodeIdx: null,
                roomName: "",
                billingStartAt: null,
                billingEndAt: null,
                modalEnergySource: null,
            },
        };
    },
    computed: {
        energySourceLabel() {
            if (this.isAll) return "전체";
            if (this.energySource === "Gas") return "가스";
            if (this.energySource === "Water") return "수도";
            return "전기";
        },
        isAll() {
            return this.energySource === "ALL";
        },
        sortedHouseholdOptions() {
            const occupied = this.households.filter((h) => h.isOccupied === "Y");
            const base = occupied.length ? occupied : this.households;
            return sortHouseholdsByRoom(base);
        },
        filteredHouseholdOptions() {
            const q = this.householdSearch.trim().toLowerCase();
            if (!q) return this.sortedHouseholdOptions;
            return this.sortedHouseholdOptions.filter((h) => String(h.roomName).toLowerCase().includes(q));
        },
        householdNavList() {
            const q = this.householdSearch.trim();
            return q ? this.filteredHouseholdOptions : this.sortedHouseholdOptions;
        },
        selectedHouseholdNavIndex() {
            return this.householdNavList.findIndex((h) => h.sysNodeIdx === this.selectedSysNodeIdx);
        },
        canPrevHousehold() {
            return this.selectedHouseholdNavIndex > 0;
        },
        canNextHousehold() {
            const i = this.selectedHouseholdNavIndex;
            return i >= 0 && i < this.householdNavList.length - 1;
        },
        timelineUsageUnit() {
            if (this.energySource === "Gas") return "Nm3";
            if (this.energySource === "Water") return "m3";
            return "kWh";
        },
        showTimelineAreaCol() {
            return this.energySource === "Elec" && !this.isAll;
        },
        monthMatrixNavRows() {
            const es = this.monthMatrixModal.matrixEnergySource;
            let rows = [...this.historyRows];
            if (es) {
                rows = rows.filter((r) => String(r.energySource) === String(es));
            }
            return rows.sort((a, b) => String(a.regYm).localeCompare(String(b.regYm)));
        },
        exportHint() {
            if (this.mode === "household") return "보내기: 세대 타임라인";
            return "보내기: 월별 요약";
        },
        canExport() {
            if (this.mode === "monthly") return this.historyRows.length > 0;
            return !!(this.householdHistory?.rows?.length);
        },
        emptyMonthlyMessage() {
            if (this.statusFilter === "ALL") return "선택한 기간에 정산 내역이 없습니다.";
            return "확정된 정산 내역이 없습니다.";
        },
    },
    watch: {
        async mode(val) {
            if (val === "household") {
                await this.prepareHouseholdMode();
            } else {
                this.load();
            }
        },
        energySource() {
            this.load();
        },
        selectedSysNodeIdx() {
            if (this.mode === "household") this.load();
        },
        statusFilter() {
            if (this.mode === "monthly") this.load();
        },
    },
    mounted() {
        this.loadHouseholds();
        if (this.mode === "household") {
            this.prepareHouseholdMode();
        } else {
            this.load();
        }
    },
    methods: {
        fmtWon,
        fmtUsage,
        fmtArea,
        fmtYm,
        unitLabel,
        statusLabel,
        statusClass,
        energyRowLabel(e) {
            if (e === "Gas") return "가스";
            if (e === "Water") return "수도";
            return "전기";
        },
        rowFloorRemainder(row) {
            return floorRemainder(row);
        },
        async loadHouseholds() {
            this.loadingHouseholds = true;
            try {
                const { data } = await backEndApi.settlement.getHouseholds(this.site.siteIdx);
                this.households = Array.isArray(data) ? data : [];
            } catch (err) {
                this.households = [];
                this.alertWarning(parseApiError(err).message || "세대 목록을 불러오지 못했습니다.");
            } finally {
                this.loadingHouseholds = false;
            }
        },
        async prepareHouseholdMode() {
            await this.loadHouseholds();
            if (!this.sortedHouseholdOptions.length) {
                this.householdHistory = null;
                return;
            }
            const valid = this.sortedHouseholdOptions.some((h) => h.sysNodeIdx === this.selectedSysNodeIdx);
            if (!this.selectedSysNodeIdx || !valid) {
                this.selectedSysNodeIdx = this.sortedHouseholdOptions[0].sysNodeIdx;
                return;
            }
            await this.load();
        },
        stepHousehold(delta) {
            const list = this.householdNavList;
            const i = this.selectedHouseholdNavIndex;
            const next = list[i + delta];
            if (next) this.selectedSysNodeIdx = next.sysNodeIdx;
        },
        pickFirstFiltered() {
            const first = this.filteredHouseholdOptions[0];
            if (first) this.selectedSysNodeIdx = first.sysNodeIdx;
        },
        async load() {
            this.loading = true;
            try {
                if (this.mode === "monthly") {
                    const { data } = await backEndApi.settlement.getHistory({
                        siteIdx: this.site.siteIdx,
                        regYmFrom: ymToRegYm(this.ymFrom),
                        regYmTo: ymToRegYm(this.ymTo),
                        energySource: this.energySource,
                        status: this.statusFilter,
                    });
                    this.historyRows = (Array.isArray(data) ? data : []).map((r) => ({
                        ...r,
                        kepcoTotalAmount: parseFloat(r.kepcoTotalAmount || 0),
                        householdSumIndividual: parseFloat(r.householdSumIndividual || 0),
                        totalAmountSum: parseFloat(r.totalAmountSum || 0),
                        commonPoolAmount: parseFloat(r.commonPoolAmount || 0),
                    }));
                } else {
                    this.householdHistory = null;
                    if (!this.selectedSysNodeIdx) return;
                    const { data } = await backEndApi.settlement.getHouseholdHistory({
                        siteIdx: this.site.siteIdx,
                        sysNodeIdx: this.selectedSysNodeIdx,
                        regYmFrom: ymToRegYm(this.ymFrom),
                        regYmTo: ymToRegYm(this.ymTo),
                        energySource: this.energySource,
                    });
                    this.householdHistory = data;
                }
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.loading = false;
            }
        },
        resolveMatrixEnergy(row) {
            return this.isAll ? row.energySource : this.energySource;
        },
        async loadMonthMatrix(regYm, matrixEnergySource) {
            const energySource = matrixEnergySource || this.energySource;
            const { data } = await backEndApi.settlement.getHistoryMatrix({
                siteIdx: this.site.siteIdx,
                regYm: String(regYm),
                energySource,
            });
            return data;
        },
        async openMonthMatrixModal(row) {
            const matrixEnergySource = this.resolveMatrixEnergy(row);
            const nav = this.buildMatrixNav(matrixEnergySource);
            const index = nav.findIndex((r) => r.settlementIdx === row.settlementIdx);
            this.monthMatrixModal = {
                show: true,
                loading: true,
                regYm: String(row.regYm),
                matrix: null,
                index,
                matrixEnergySource,
            };
            try {
                this.monthMatrixModal.matrix = await this.loadMonthMatrix(row.regYm, matrixEnergySource);
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
                this.monthMatrixModal.show = false;
            } finally {
                this.monthMatrixModal.loading = false;
            }
        },
        buildMatrixNav(matrixEnergySource) {
            let rows = [...this.historyRows];
            if (matrixEnergySource) {
                rows = rows.filter((r) => String(r.energySource) === String(matrixEnergySource));
            }
            return rows.sort((a, b) => String(a.regYm).localeCompare(String(b.regYm)));
        },
        async stepMonthMatrix(delta) {
            const es = this.monthMatrixModal.matrixEnergySource;
            const nav = this.buildMatrixNav(es);
            const next = nav[this.monthMatrixModal.index + delta];
            if (!next) return;
            this.monthMatrixModal.index += delta;
            this.monthMatrixModal.loading = true;
            this.monthMatrixModal.regYm = String(next.regYm);
            try {
                this.monthMatrixModal.matrix = await this.loadMonthMatrix(next.regYm, es);
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.monthMatrixModal.loading = false;
            }
        },
        openHouseholdModal(row, regYm, billingStartAt, billingEndAt, modalEnergySource) {
            this.householdModal = {
                show: true,
                regYm: String(regYm),
                sysNodeIdx: row.sysNodeIdx ?? this.selectedSysNodeIdx,
                roomName: row.roomName || this.householdHistory?.roomName || "",
                billingStartAt: billingStartAt || null,
                billingEndAt: billingEndAt || null,
                modalEnergySource: modalEnergySource || (this.isAll ? null : this.energySource),
            };
        },
        openHouseholdModalFromTimeline(row) {
            const modalEnergySource = this.isAll ? row.energySource : this.energySource;
            this.openHouseholdModal(
                { sysNodeIdx: this.selectedSysNodeIdx, roomName: this.householdHistory?.roomName },
                row.regYm,
                row.billingStartAt,
                row.billingEndAt,
                modalEnergySource,
            );
        },
        openHouseholdModalFromMonthMatrix(row) {
            const hist = this.buildMatrixNav(this.monthMatrixModal.matrixEnergySource)[this.monthMatrixModal.index];
            const m = this.monthMatrixModal.matrix;
            this.openHouseholdModalWithBilling(
                row,
                this.monthMatrixModal.regYm,
                m?.settlementIdx || hist?.settlementIdx,
                this.monthMatrixModal.matrixEnergySource,
            );
        },
        async openHouseholdModalWithBilling(row, regYm, settlementIdx, modalEnergySource) {
            let billingStartAt = null;
            let billingEndAt = null;
            if (settlementIdx) {
                try {
                    const { data } = await backEndApi.settlement.getSettlement(settlementIdx);
                    billingStartAt = data.billingStartAt;
                    billingEndAt = data.billingEndAt;
                } catch (err) {
                    /* ignore */
                }
            }
            this.openHouseholdModal(row, regYm, billingStartAt, billingEndAt, modalEnergySource);
        },
        async exportCsv() {
            this.exporting = true;
            try {
                if (this.mode === "monthly") {
                    const { data } = await backEndApi.settlement.exportHistory({
                        siteIdx: this.site.siteIdx,
                        regYmFrom: ymToRegYm(this.ymFrom),
                        regYmTo: ymToRegYm(this.ymTo),
                        energySource: this.energySource,
                    });
                    const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `settlement_detail_${this.site.siteIdx}_${ymToRegYm(this.ymFrom)}_${ymToRegYm(this.ymTo)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                } else if (this.householdHistory?.rows?.length) {
                    const lines = buildHouseholdCsvRows(this.householdHistory);
                    downloadCsvBlob(
                        `settlement_household_${this.householdHistory.sysNodeIdx}_${ymToRegYm(this.ymFrom)}_${ymToRegYm(this.ymTo)}.csv`,
                        lines,
                    );
                }
            } catch (err) {
                this.alertWarning(parseApiError(err).message || "보내기 실패");
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>

<style scoped>
.stl-household-picker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.stl-ledger-help {
    margin: 0 0 14px;
    padding: 10px 14px;
    font-size: 12.5px;
    line-height: 1.5;
    color: #5c6578;
    background: #f7f8fb;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
}
</style>
